import Icon from "@/components/feature/Icon";
import SmartImage from "@/components/feature/SmartImage";
import { usePublicRows } from "@/hooks/usePublicContent";
import { useSiteSettings } from "@/lib/siteSettings";
import type { DbMemberDirectoryEntry } from "@/types/database";

const NO_FALLBACK: DbMemberDirectoryEntry[] = [];

/**
 * 會員名錄：由後台「會員名錄」維護，只顯示已勾選「已取得公開同意」且顯示中的單位。
 * 尚無資料時顯示「整理中」。
 */
export default function MembershipDirectory() {
  const { rows, loading } = usePublicRows<DbMemberDirectoryEntry>("member_directory", NO_FALLBACK, {
    refine: (q) => q.eq("consent", true),
  });
  const { settings } = useSiteSettings();
  const applicationsOpen = settings.membership.applications_open;

  const categories = Array.from(new Set(rows.map((r) => r.category)));

  return (
    <section className="py-24 md:py-32 bg-silver-50">
      <div className="px-6 md:px-10 lg:px-16">
        <div className="max-w-3xl mb-12 md:mb-16">
          <p className="text-sm font-medium tracking-[0.2em] text-navy-700 uppercase mb-3">
            Member Directory
          </p>
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-semibold text-navy-950 leading-tight">
            會員名錄
          </h2>
        </div>

        {!loading && rows.length > 0 ? (
          <div className="space-y-12" data-testid="member-directory">
            {categories.map((cat) => (
              <div key={cat}>
                <h3 className="text-sm font-medium tracking-[0.2em] text-navy-700 uppercase mb-5">{cat}</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
                  {rows
                    .filter((r) => r.category === cat)
                    .map((m) => {
                      const card = (
                        <>
                          <div className="h-16 flex items-center justify-center mb-4">
                            {m.logo_url ? (
                              <SmartImage src={m.logo_url} alt={m.name} className="max-h-16 max-w-full object-contain" fallbackText={m.name} />
                            ) : (
                              <div className="w-14 h-14 rounded-2xl bg-navy-950/5 flex items-center justify-center">
                                <Icon className="ri-building-4-line text-2xl text-navy-950" />
                              </div>
                            )}
                          </div>
                          <p className="text-navy-950 font-semibold text-sm md:text-base text-center leading-snug">{m.name}</p>
                          {m.description && (
                            <p className="mt-2 text-xs text-silver-500 text-center leading-relaxed line-clamp-2">{m.description}</p>
                          )}
                        </>
                      );
                      const classes =
                        "bg-white rounded-3xl border border-silver-200 p-6 flex flex-col items-center hover:border-navy-200 hover:shadow-lg transition-all duration-300";
                      return m.website_url ? (
                        <a key={m.id} href={m.website_url} target="_blank" rel="noopener noreferrer" className={classes}>
                          {card}
                        </a>
                      ) : (
                        <div key={m.id} className={classes}>
                          {card}
                        </div>
                      );
                    })}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-3xl border border-silver-200 p-8 md:p-12 flex flex-col items-center text-center">
            <div className="w-14 h-14 rounded-2xl bg-navy-950/5 flex items-center justify-center mb-5">
              <Icon className="ri-team-line text-2xl text-navy-950" />
            </div>
            <h3 className="text-xl md:text-2xl font-semibold text-navy-950 mb-3">
              會員名錄整理中
            </h3>
            <p className="text-silver-500 text-sm md:text-base leading-relaxed max-w-xl">
              正式會員名錄正在彙整，將於取得各會員單位同意公開後陸續更新。
              {applicationsOpen ? "歡迎先透過入會申請加入 TAIAA。" : "會員採邀請制，有意加入者歡迎與秘書處聯繫。"}
            </p>
          </div>
        )}
      </div>
    </section>
  );
}
